import { memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ReactComponent as Loading } from '~/assets/img/loading/loading.svg';

function FullScreenLoading({
  bgTransparent = false,
}: {
  bgTransparent?: boolean;
}) {
  useEffect(() => {
    const preventScroll = (e: Event) => e.preventDefault();

    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, {
      passive: false,
    });

    return () => {
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  return createPortal(
    <div
      css={css`
        display: flex;
        position: fixed;
        z-index: 9999;
        top: 0;
        left: 0;
        align-items: center;
        justify-content: center;
        width: 100vw;
        height: 100vh;
        background-color: ${bgTransparent
          ? 'transparent'
          : 'rgba(0, 0, 0, 0.2)'};
      `}
    >
      <Container>
        <LoadingContainer>
          <LoadingSpinner>
            <Loading />
          </LoadingSpinner>
        </LoadingContainer>
        <LoadingText>Loading</LoadingText>
      </Container>
    </div>,
    document.body,
  );
}

export default memo(FullScreenLoading);

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 30%;
    width: 98px;
    padding: 16px 24px;
    border-radius: 16px;
    background: ${theme.backgroundColor.bg_dark_4_alpha};
    gap: 8px;
    backdrop-filter: blur(50px);
  `}
`;

const LoadingText = styled.span`
  ${({ theme }) => css`
    ${theme.fontStyle.text.body14_medium}
    color: ${theme.content.content_light_1};
  `}
`;

const LoadingContainer = styled.div`
  ${({ theme }) => css`
    position: relative;

    &::before {
      content: '';
      display: block;
      position: absolute;
      box-sizing: border-box;
      width: 100%;
      height: 100%;
      border: 4px solid ${theme.backgroundColor.bg_light_5_alpha};
      border-radius: 50%;
    }
  `}
`;

const LoadingSpinner = styled.i`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44.5px;
    height: 44.5px;
    animation: l2 1.3s infinite linear;
    color: ${theme.themeColor.main_1};
    filter: drop-shadow(0px 0px 8px ${theme.themeColor.main_1});
    @keyframes l2 {
      to {
        transform: rotate(1turn);
      }
    }

    & > svg {
      width: 100%;
      height: 100%;
    }
  `}
`;
